import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { darken, setLightness, parseToRgb, parseToHsl, rgba, mix, getLuminance, toColorString } from 'polished';
import { getCSSProperty } from '../../utils';

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function setAlphaChannel(alpha, color) {
  if (typeof alpha !== 'undefined') {
    var _parseToRgb = parseToRgb(color),
        red = _parseToRgb.red,
        green = _parseToRgb.green,
        blue = _parseToRgb.blue;

    return "rgba(" + red + ", " + green + ", " + blue + ", " + alpha + ")";
  } else {
    return color;
  }
}
export function generateColorScale(_ref) {
  var _ref2;

  var key = _ref.key,
      baseColor = _ref.baseColor;
  return _ref2 = {}, _ref2[key + "-lightest"] = setLightness(0.94, baseColor), _ref2[key + "-lighter"] = setLightness(0.92, baseColor), _ref2[key + "-light"] = setLightness(0.7, baseColor), _ref2[key] = baseColor, _ref2[key + "-dark"] = darken(0.04, baseColor), _ref2[key + "-darker"] = darken(0.08, baseColor), _ref2[key + "-darkest"] = darken(0.16, baseColor), _ref2;
}
export function eachPalette(palette, getColor) {
  return Object.keys(palette).map(function (key) {
    var colorSet = palette[key];
    var nextColors;

    if (typeof colorSet === 'string') {
      var _nextColors;

      nextColors = (_nextColors = {}, _nextColors[key] = colorSet, _nextColors);
    } else if (Array.isArray(colorSet)) {
      // deal with scale values: color, color-0, color-1, etc.
      nextColors = colorSet.reduce(function (flatColors, color, index) {
        var _objectSpread2;

        return _objectSpread(_objectSpread({}, flatColors), {}, (_objectSpread2 = {}, _objectSpread2[key + "-" + index] = color, _objectSpread2));
      }, {});
    } else {
      // if palette contains a "base" key spread under key name: primary, primary-light, etc.
      if (colorSet.base) {
        nextColors = Object.keys(colorSet).reduce(function (flatColors, colorSetKey) {
          var _objectSpread3;

          return _objectSpread(_objectSpread({}, flatColors), {}, (_objectSpread3 = {}, _objectSpread3[colorSetKey === 'base' ? key : key + "-" + colorSetKey] = colorSet[colorSetKey], _objectSpread3));
        }, {});
      } else {
        nextColors = Object.keys(colorSet).reduce(function (flatColors, colorSetKey) {
          var _objectSpread4;

          return _objectSpread(_objectSpread({}, flatColors), {}, (_objectSpread4 = {}, _objectSpread4[colorSetKey] = colorSet[colorSetKey], _objectSpread4));
        }, {});
      }
    }

    return getColor({
      name: capitalizeFirstLetter(key),
      colors: nextColors
    });
  });
}
export function flattenPalette(palette) {
  var parsedPalette = eachPalette(palette, function (_ref3) {
    var colors = _ref3.colors;
    return colors;
  }).reduce(function (prevColors, nextColors) {
    return _objectSpread(_objectSpread({}, prevColors), nextColors);
  }, {}); // now that we have a resolved collection, run through it again to fill in any aliased colors

  return Object.keys(parsedPalette).reduce(function (collection, key) {
    var _objectSpread5;

    var value = parsedPalette[key];
    var aliasValue = parsedPalette[value];
    return _objectSpread(_objectSpread({}, collection), {}, (_objectSpread5 = {}, _objectSpread5[key] = aliasValue ? getCSSProperty('colors', value) : value, _objectSpread5));
  }, {});
} // ported from ChromaJS
// https://github.com/gka/chroma.js/blob/d2c6d917df4ba2b87d8a740de116a0656bcbdfd5/src/io/lightness.coffee

var EPS = 1e-7;
export function adjustLightness(amount, color) {
  var _parseToRgb2 = parseToRgb(color),
      _parseToRgb2$alpha = _parseToRgb2.alpha,
      alpha = _parseToRgb2$alpha === void 0 ? 1 : _parseToRgb2$alpha;

  var rgb;

  if (amount === 0) {
    rgb = rgba(0, 0, 0, alpha);
  } else if (amount === 1) {
    rgb = rgba(255, 255, 255, alpha);
  } else {
    var maxIteration = 20;

    var test = function test(color1, color2) {
      var mixed = mix(0.5, color1, color2);
      var mixedlightness = getLuminance(mixed);

      if (Math.abs(amount - mixedlightness) < EPS || !maxIteration--) {
        return mixed;
      }

      if (mixedlightness > amount) {
        return test(color1, mixed);
      }

      return test(mixed, color2);
    };

    rgb = getLuminance(color) > amount ? test('#000', color) : test(color, '#fff');
  }

  return rgb;
}
export function adjustSaturation(amount, color) {
  var hsl = parseToHsl(color);
  hsl.saturation += hsl.saturation * amount;
  return toColorString(hsl);
} // Color pallette functions

export function createTone(color, desaturate) {
  if (desaturate === void 0) {
    desaturate = 0.5;
  }

  return function (value) {
    // we clamp the number so we can generate better shades if 0 or 1 are passed,
    // otherwise these colors would be pure black or white
    var lightnessAmount = Math.min(Math.max(value, 0.05), 0.95);
    return adjustSaturation(-value * desaturate, adjustLightness(lightnessAmount, color));
  };
} // calculations repurposed from jxnblk
// https://github.com/jxnblk/monochrome/blob/master/src/palette.js#L22-L34

export function createTones(color, desaturate) {
  var tone = createTone(color, desaturate);
  var lightness = getLuminance(color);
  var lowerstep = lightness / 5;
  var upperstep = (1 - lightness) / 6;
  var lower = [4, 3, 2, 1].map(function (step) {
    return tone(step * lowerstep);
  });
  var upper = [5, 4, 3, 2, 1, 0].map(function (step) {
    return tone(lightness + step * upperstep);
  });
  return [].concat(upper, lower);
}
export function createColorScale(color, desaturate) {
  return {
    base: color,
    text: getLuminance(color) < 0.5 ? '#fff' : '#000',
    tone: createTone(color, desaturate),
    tones: createTones(color, desaturate)
  };
}
export function createColorScales(colors, desaturate) {
  function colorReducer(parsedColors, key) {
    var _objectSpread6;

    return _objectSpread(_objectSpread({}, parsedColors), {}, (_objectSpread6 = {}, _objectSpread6[key] = createColorScale(colors[key], desaturate), _objectSpread6));
  }

  return Object.keys(colors).reduce(colorReducer, {});
}
export function flattenColorScales(colors) {
  return Object.keys(colors).reduce(function (parsedColors, key) {
    var color = colors[key];
    parsedColors[key] = color.base;
    color.tones.forEach(function (tone, index) {
      parsedColors[key + "-" + index] = tone;
    });
    return parsedColors;
  }, {});
}